import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CustomerMailDialog } from "@/components/CustomerMailDialog";
import { PartnerListExport } from "@/components/PartnerListExport";
import { useAuth } from "@/context/AuthContext";
import { Plus, Mail, Search, Archive, CheckCircle2, ChevronRight, History, Handshake } from "lucide-react";
import { statusMap } from "@/lib/workflow";

const REQ_MAP = statusMap("bezahlkunde");

export const TYPE_META = {
  partner: { path: "/partner", title: "Partner / Sponsoren", sub: "Umsetzung der Marketing-Leistungen – Sponsoren mit Vertragsleistungen, Partner mit gebuchten Werbeflächen.", one: "Partner" },
  bezahlkunde: { path: "/anzeigenkunden", title: "Anzeigenkunden", sub: "Kunden, die bei uns Anzeigen geschaltet haben – Anfragen für neue Anzeigen starten.", one: "Anzeigenkunde" },
};

export const KIND_META = {
  sponsor: ["Sponsor", "bg-amber-100 text-amber-800 border-amber-200"],
  partner: ["Partner", "bg-sky-100 text-sky-800 border-sky-200"],
};

export function KindBadge({ kind, id }) {
  if (!kind) return null;
  const [l, cls] = KIND_META[kind] || KIND_META.sponsor;
  return <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`} data-testid={`kind-badge-${id}`}>{l}</span>;
}

export const TIER_META = {
  gold: ["Gold", "bg-yellow-100 text-yellow-800 border-yellow-300"],
  silber: ["Silber", "bg-slate-100 text-slate-700 border-slate-300"],
  bronze: ["Bronze", "bg-orange-100 text-orange-800 border-orange-300"],
};

export function TierBadge({ tier, id }) {
  if (!TIER_META[tier]) return null;
  const [l, cls] = TIER_META[tier];
  return <span className={`inline-block ml-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`} data-testid={`tier-badge-${id}`}>{l}</span>;
}

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "");
const missing = (c, key) => (c.services || []).some((s) => (s.space_ids || []).some((sid) => !s.state?.[sid]?.[key]));

export const contactName = (c) => [c.salutation, c.title, c.first_name, c.last_name].filter(Boolean).join(" ") || c.contact_name || "";

function Validity({ c }) {
  if (!c.valid_until && !c.valid_from) return <span className="text-slate-400">–</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${c.expired ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
      {c.expired ? <Archive size={12} /> : <CheckCircle2 size={12} />}
      {c.expired ? "Abgelaufen " : "Gültig "}{c.valid_from ? `${fmtDate(c.valid_from)} – ` : "bis "}{fmtDate(c.valid_until) || "offen"}
    </span>
  );
}

function ReqStatus({ row, id }) {
  if (!row) return <span className="text-slate-400">–</span>;
  const [l, cls] = REQ_MAP[row.du_status] || REQ_MAP.offen;
  const last = row.mails?.length ? row.mails[row.mails.length - 1].at : null;
  return (
    <div data-testid={`customer-req-${id}`}>
      <span className={`text-[11px] font-semibold rounded px-2 py-0.5 border ${cls}`}>{l}</span>
      {last && <div className="text-xs text-slate-400 mt-1">Mail {fmtDate(last)}</div>}
    </div>
  );
}

export default function CustomerList({ type }) {
  const meta = TYPE_META[type];
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("aktiv");
  const [mailFor, setMailFor] = useState(null);
  const [sel, setSel] = useState([]);
  const { user } = useAuth();
  const canMail = !!user?.is_chef;

  const [wf, setWf] = useState({});
  const [noMail, setNoMail] = useState(false);
  const [freeAd, setFreeAd] = useState("");
  const [kind, setKind] = useState("");
  const [tier, setTier] = useState("");
  const [gap, setGap] = useState("");
  const reload = () => {
    api.get(`/customers?type=${type}`).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    if (type === "bezahlkunde") {
      api.get("/customers-workflow", { params: { type, all: true } }).then((r) => setWf({ issue: r.data.issue, rows: Object.fromEntries(r.data.rows.map((x) => [x.id, x])) })).catch(() => {});
    }
  };
  useEffect(() => { reload(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener("mailjobs-done", reload);
    return () => window.removeEventListener("mailjobs-done", reload);
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const archived = items.filter((c) => c.archived);
  const filtered = items.filter((c) => (tab === "archiv") === !!c.archived
    && (!noMail || !c.email)
    && (!freeAd || (freeAd === "mit") === !!c.ad_format_id)
    && (!kind || c.partner_kind === kind)
    && (!tier || (c.tier || "") === tier)
    && (!gap || missing(c, gap))
    && (!q || `${c.name} ${contactName(c)} ${c.email} ${c.city}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (type === "bezahlkunde" ? (b.last_invoice ? 1 : 0) - (a.last_invoice ? 1 : 0) : 0));
  const open = (c) => navigate(`${meta.path}/${c.id}`, { state: { ids: filtered.map((x) => x.id) } });
  const eligible = filtered.filter((c) => c.email && !c.archived);
  const allOn = eligible.length > 0 && eligible.every((c) => sel.includes(c.id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSel(allOn ? [] : eligible.map((c) => c.id));
  const selected = items.filter((c) => sel.includes(c.id));

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{meta.title}</h1>
          <p className="text-slate-500 mt-1">{meta.sub}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {type === "partner" && <PartnerListExport list={filtered} label={[tab === "archiv" ? "Archiv" : "", kind === "sponsor" ? "nur Sponsoren" : kind === "partner" ? "nur Partner" : "", TIER_META[tier] ? `nur ${TIER_META[tier][0]}` : "", gap === "material" ? "Material fehlt" : gap === "active" ? "noch nicht umgesetzt" : "", freeAd === "mit" ? "mit Freianzeige" : freeAd === "ohne" ? "ohne Freianzeige" : "", noMail ? "ohne E-Mail" : "", q ? `Suche „${q}“` : ""].filter(Boolean).join(", ")} />}
          <Button onClick={() => navigate(`${meta.path}/neu`)} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-customer-button">
            <Plus size={16} className="mr-1.5" /> {meta.one} anlegen
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        {(
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {[["aktiv", `Aktiv (${items.length - archived.length})`], ["archiv", `Archiv (${archived.length})`]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`customer-tab-${k}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {type === "partner" && [["sponsor", "Sponsoren"], ["partner", "Partner"]].map(([k, l]) => (
            <button key={k} onClick={() => { setKind((v) => (v === k ? "" : k)); setSel([]); }} data-testid={`customer-filter-kind-${k}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${kind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {l} ({items.filter((c) => (tab === "archiv") === !!c.archived && c.partner_kind === k).length})
            </button>
          ))}
          {type === "partner" && <span className="w-px h-6 bg-slate-200 mx-1" />}
          {type === "partner" && [["gold", "Gold"], ["silber", "Silber"], ["bronze", "Bronze"]].map(([k, l]) => (
            <button key={k} onClick={() => { setTier((v) => (v === k ? "" : k)); setSel([]); }} data-testid={`customer-filter-tier-${k}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${tier === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {l} ({items.filter((c) => (tab === "archiv") === !!c.archived && c.tier === k).length})
            </button>
          ))}
          {type === "partner" && <span className="w-px h-6 bg-slate-200 mx-1" />}
          {type === "partner" && [["material", "Material fehlt"], ["active", "Noch nicht umgesetzt"]].map(([k, l]) => (
            <button key={k} onClick={() => { setGap((v) => (v === k ? "" : k)); setSel([]); }} data-testid={`customer-filter-gap-${k}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${gap === k ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {l} ({items.filter((c) => (tab === "archiv") === !!c.archived && missing(c, k)).length})
            </button>
          ))}
          {type === "partner" && <span className="w-px h-6 bg-slate-200 mx-1" />}
          {type === "partner" && [["mit", "Mit Freianzeige"], ["ohne", "Ohne Freianzeige"]].map(([k, l]) => (
            <button key={k} onClick={() => { setFreeAd((v) => (v === k ? "" : k)); setSel([]); }} data-testid={`customer-filter-${k}-free-ad`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${freeAd === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {l} ({items.filter((c) => (tab === "archiv") === !!c.archived && (k === "mit") === !!c.ad_format_id).length})
            </button>
          ))}
          <button onClick={() => setNoMail((v) => !v)} data-testid="customer-filter-no-email"
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${noMail ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            Ohne E-Mail ({items.filter((c) => (tab === "archiv") === !!c.archived && !c.email).length})
          </button>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="pl-9 w-64" data-testid="customer-search" />
        </div>
        </div>
      </div>

      {canMail && sel.length > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 rounded-lg border border-blue-200 bg-blue-50" data-testid="bulk-mail-bar">
          <span className="text-sm font-medium text-slate-800">{sel.length} ausgewählt</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSel([])} data-testid="bulk-clear">Auswahl aufheben</Button>
            <Button size="sm" onClick={() => setMailFor(selected)} className="bg-blue-600 hover:bg-blue-700" data-testid="bulk-mail-button">
              <Mail size={14} className="mr-1.5" /> E-Mail an {sel.length} Empfänger
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              {canMail && <th className="pl-4 py-3 w-8"><input type="checkbox" checked={allOn} onChange={toggleAll} title="Alle auswählen" className="h-4 w-4 accent-blue-600 cursor-pointer" data-testid="select-all-customers" /></th>}
              <th className="text-left px-4 py-3">Firma</th>
              <th className="text-left px-4 py-3">Anzeigenformat</th>
              <th className="text-left px-4 py-3">{type === "partner" ? "Partnerschaft" : "Preis"}</th>
              {type === "bezahlkunde" && <th className="text-left px-4 py-3" data-testid="customers-issue-col">{wf.issue ? wf.issue.name : "Ausgabe"}</th>}
              <th className="text-left px-4 py-3">Kontakt</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => open(c)} className={`cursor-pointer hover:bg-slate-50 ${c.archived ? "bg-red-50/40" : ""}`} data-testid={`customer-row-${c.id}`}>
                {canMail && <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={sel.includes(c.id)} disabled={!c.email || c.archived} onChange={() => toggle(c.id)}
                    title={!c.email ? "Keine E-Mail-Adresse" : c.inactive ? "Deaktiviert" : c.expired ? "Partnerschaft abgelaufen" : "Auswählen"} className="h-4 w-4 accent-blue-600 cursor-pointer disabled:opacity-30" data-testid={`select-customer-${c.id}`} />
                </td>}
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{c.name}{c.last_invoice && <span title={`Vorjahr gebucht: ${c.last_invoice.subject} · ${c.last_invoice.no} · ${c.last_invoice.net.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € netto`} className="ml-1.5 inline-flex align-middle text-emerald-600" data-testid={`customer-prev-year-${c.id}`}><History size={15} /></span>}{c.former_partner_id && <span title={`Ehemaliger Partner bis ${new Date(c.former_partner_until).toLocaleDateString("de-DE")}`} className="ml-1.5 inline-flex align-middle text-blue-600" data-testid={`customer-former-partner-${c.id}`}><Handshake size={15} /></span>}</div>
                  <div className="text-xs text-slate-500">{contactName(c)}</div>
                  {type === "partner" && <div className="mt-1"><KindBadge kind={c.partner_kind} id={c.id} /><TierBadge tier={c.tier} id={c.id} /></div>}
                  {c.inactive && <span className="inline-block mt-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" data-testid={`customer-inactive-${c.id}`}>Deaktiviert</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{c.ad_format || "–"}{c.ad_size ? <div className="text-xs text-slate-400">{c.ad_size}</div> : null}</td>
                <td className="px-4 py-3 text-slate-600">
                  {type === "partner" ? <Validity c={c} /> : (c.amount ? `${c.amount} € netto` : "–")}
                </td>
                {type === "bezahlkunde" && <td className="px-4 py-3"><ReqStatus row={wf.rows?.[c.id]} id={c.id} /></td>}
                <td className="px-4 py-3 text-slate-600"><div>{c.email || "–"}</div><div className="text-xs text-slate-400">{c.phone}</div></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" disabled={!c.email || c.archived || !canMail} title={!canMail ? "Keine Berechtigung für den Mailversand" : c.inactive ? "Deaktiviert – Versand gesperrt" : c.expired ? "Partnerschaft abgelaufen – Versand gesperrt" : "E-Mail senden"}
                      onClick={(e) => { e.stopPropagation(); setMailFor([c]); }} data-testid={`customer-mail-${c.id}`}>
                      <Mail size={16} className="text-blue-600" />
                    </Button>
                    <ChevronRight size={16} className="text-slate-400" />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Keine Einträge gefunden.</td></tr>}
          </tbody>
        </table>
      </div>
      <CustomerMailDialog customers={mailFor} onClose={(done) => { if (done && mailFor?.length > 1) setSel([]); setMailFor(null); if (done) reload(); }} />
    </div>
  );
}
